import React, { useMemo } from 'react';
import { DayScale } from './scale';
import { timelineColors, timelineFontWeight } from './tokens';

const WEEK_LABEL_FONT_SIZE = 12;
// Narrowest full week that still fits a date-range label such as
// `09/30-10/06` at WEEK_LABEL_FONT_SIZE / medium weight (measured ≈ 68 px).
// Below this the header uses a label's `short` form when one is offered.
const WEEK_LABEL_FULL_MIN_PX = 70;

/** A label with a fallback for narrow columns; the header picks which to show. */
export type WeekLabelForms = { full: React.ReactNode; short: React.ReactNode };

export type WeekLabelRenderer = (
  weekIndex: number,
  weekStartDay: number
) => React.ReactNode | WeekLabelForms;

const hasForms = (
  label: React.ReactNode | WeekLabelForms
): label is WeekLabelForms =>
  typeof label === 'object' &&
  label !== null &&
  !React.isValidElement(label) &&
  'full' in label;

export const WeekHeader = React.memo(
  ({
    scale,
    height,
    renderLabel
  }: {
    scale: DayScale;
    height: number;
    // Week numbering is intentionally left to the caller: which week counts
    // as "Week 1" depends on the anchor semantics of the surface rendering
    // this (e.g. experience start vs. the anchor date), which this pure
    // engine component has no opinion on. A caller may return `{ full, short }`
    // and let this header choose by column width, since the font that decides
    // what fits lives here. Keep the callback referentially stable (useCallback)
    // so this memoised header doesn't re-render on unrelated view changes.
    renderLabel?: WeekLabelRenderer;
  }) => {
    const label: WeekLabelRenderer = renderLabel ?? ((i: number) => `Week ${i}`);
    // Compared against a full week, not the clipped visible width, so a trailing
    // partial week doesn't switch form on its own.
    const useShort = 7 * scale.colWidth < WEEK_LABEL_FULL_MIN_PX;
    const pick = (l: React.ReactNode | WeekLabelForms) =>
      hasForms(l) ? (useShort ? l.short : l.full) : l;

    const weeks = useMemo(() => {
      const out: { key: string; x: number; width: number; weekStart: number; index: number }[] = [];
      const firstWeekStart = Math.floor(scale.dayMin / 7) * 7;
      let index = 1;
      for (let weekStart = firstWeekStart; weekStart <= scale.dayMax; weekStart += 7) {
        const visibleStart = Math.max(weekStart, scale.dayMin);
        const visibleEnd = Math.min(weekStart + 6, scale.dayMax);
        const x = scale.xOfDayEdge(visibleStart);
        const xEnd = scale.xOfDayEdge(visibleEnd + 1);
        out.push({ key: `week-${weekStart}`, x, width: xEnd - x, weekStart, index });
        index++;
      }
      return out;
    }, [scale]);

    return (
      <>
        {weeks.map((w) => (
          <div
            key={w.key}
            style={{
              position: 'absolute',
              left: w.x,
              top: 0,
              width: w.width,
              height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: WEEK_LABEL_FONT_SIZE,
              fontWeight: timelineFontWeight.medium,
              color: timelineColors.textSubtle,
              borderBottom: `1px solid ${timelineColors.border}`
            }}
          >
            {pick(label(w.index, w.weekStart))}
          </div>
        ))}
      </>
    );
  }
);
